'use client';

import { useState, useRef, useEffect } from 'react';
import type { Category } from './categories-page-new';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';

type OverflowMeasurement = {
  description: string;
  isOverflowing: boolean;
  slug: string;
};

export function CategoryHeaderDescription({ category }: { category: Category }) {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [overflowMeasurement, setOverflowMeasurement] = useState<OverflowMeasurement | null>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const description = category.description ?? '';
  const hasMeasuredCurrentDescription =
    overflowMeasurement?.description === description && overflowMeasurement.slug === category.slug;
  const isOverflowing = hasMeasuredCurrentDescription ? overflowMeasurement.isOverflowing : false;
  const isCurrentDescriptionExpanded = hasMeasuredCurrentDescription && isExpanded;
  const showExpandButton = !isCurrentDescriptionExpanded && isOverflowing;

  useEffect(() => {
    setIsExpanded(false);

    const element = descriptionRef.current;
    const lineHeight = element ? Number.parseFloat(getComputedStyle(element).lineHeight) : 0;
    const maxHeight = Number.isFinite(lineHeight) ? lineHeight * 4 : (element?.clientHeight ?? 0);

    setOverflowMeasurement({
      description,
      isOverflowing: Boolean(element && element.scrollHeight > maxHeight),
      slug: category.slug,
    });
  }, [category.slug, description]);

  return (
    <div className="relative w-full">
      <HtmlRenderer
        refDiv={descriptionRef}
        html={description}
        className={cn(
          'w-full text-left text-[#838383]',
          !isCurrentDescriptionExpanded && 'line-clamp-4',
        )}
      />

      {!isCurrentDescriptionExpanded && (
        <div
          aria-hidden={!showExpandButton}
          className={cn(
            'absolute bottom-0 left-0 right-0 flex justify-center',
            !showExpandButton && 'pointer-events-none invisible',
          )}
        >
          <div
            className="pointer-events-none absolute bottom-0 h-16 w-full bg-gradient-to-t from-white to-transparent"
            style={{ zIndex: 1 }}
          />
          <Button
            variant="dark"
            className="relative mb-2 max-h-8 py-0"
            style={{ zIndex: 2 }}
            disabled={!showExpandButton}
            tabIndex={showExpandButton ? 0 : -1}
            onClick={() => setIsExpanded(true)}
          >
            Celý popis
          </Button>
        </div>
      )}
      {isCurrentDescriptionExpanded && (
        <div className="mt-2 flex justify-center">
          <Button variant="dark" className="max-h-8 py-0" onClick={() => setIsExpanded(false)}>
            Skryť popis
          </Button>
        </div>
      )}
    </div>
  );
}
